import { type RefObject, useEffect, useId, useRef, useState } from "react";

import { CommandFeedback } from "components/CommandFeedback";
import { Button } from "components/ui/button";

import { eventDays, staleDayTitles } from "../day-titles";
import {
  dayLabel,
  type DayRange,
  type OutsideDates,
  outsideDatesMessage,
  rangeLabel,
  setDatesLabel,
  shortensEvent,
  tooLongMessage,
} from "../event-dates-fit";
import type { EventSettings } from "../schemas";
import { useEventSettingsWrite } from "../use-event-settings-writes";

import { AfterEventNotificationTimeCheck } from "./AfterEventSettings";
import { WarningNote } from "./WarningNote";

/**
 * 44px tall and the width of the box on a phone, where the label wraps onto a
 * second line instead of running past the edge at 320px.
 */
const WRAPPING_BUTTON =
  "h-11 max-sm:h-auto max-sm:min-h-11 max-sm:w-full max-sm:whitespace-normal max-sm:py-2 sm:h-10";

type Step = "idle" | "confirming" | "done";

/**
 * Sessions on days the event does not have, and the one button that fixes it.
 *
 * Shared by the Agenda tab (sessions already saved) and the CSV import panel
 * (rows about to be). The dates are written through the same event settings
 * write the Settings tab uses, `startDate` and `endDate` only, so the rules
 * check them as they check any other change of dates. The button asks once,
 * in line, before it writes.
 *
 * Keyboard and screen readers. The note itself is not a live region, because
 * it holds buttons and changes in place, and a live region re-reads all of
 * that on every change. Focus does the moving instead: opening the question
 * puts focus on it, Cancel and a refused write put it back on the button, and
 * a saved write puts it on the result, and dismissing the result hands it
 * to the button the page names (Add item, Import), or to the page's fallback
 * when that button is disabled. The short texts that matter (the warning
 * when it appears, saving, saved) go through one polite region, as in the
 * day titles editor, since a region that appears with its text is often not
 * read. That region is the same element in every state, so a screen reader
 * already has it when the text arrives. A refused write is the one urgent
 * text: it goes into an alert, also always mounted and empty until then,
 * since some screen readers miss an alert inserted together with its text,
 * and the polite region stays empty meanwhile. The visible feedback underneath stays
 * silent, so nothing is read twice.
 *
 * The warning is announced when it appears, or when the days it is about
 * change, after the note is on the page, such as after a CSV file is chosen.
 * A new count of the same days is not announced, so an import writing rows in
 * batches does not re-read it, and nothing is announced while the import
 * writes, while the question is open, or when the page first loads with it.
 *
 * It stays mounted after the write: the event is read again and the warning
 * goes away with it, so the result has to be said by something that is still
 * there. Day titles are not touched here. Titles for days the new dates no
 * longer include are dropped when the day titles are next saved, and the note
 * says so.
 */
export function OutsideDatesNote({
  eventId,
  uid,
  timeZone,
  event,
  reload,
  info,
  subject,
  testId,
  returnFocusRef,
  fallbackFocusRef,
  busy = false,
}: {
  eventId: string;
  uid: string | undefined;
  /** The event's zone when this browser can resolve it. */
  timeZone: string | undefined;
  event: Pick<EventSettings, "startDate" | "endDate" | "dayTitles">;
  /** Reads the event again once its dates are written. */
  reload: () => void;
  /** What is outside, or null when nothing is. */
  info: OutsideDates | null;
  subject: "agenda" | "file";
  testId: string;
  /** Takes focus when the saved result is dismissed, such as Add or Import. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Takes focus instead when that one is missing or disabled. */
  fallbackFocusRef?: RefObject<HTMLElement | null>;
  /** The page is writing (an import), so a changed warning is not announced. */
  busy?: boolean;
}) {
  const { save, saving, error, clearError } = useEventSettingsWrite(eventId);
  const [step, setStep] = useState<Step>("idle");
  const [result, setResult] = useState<{
    range: DayRange;
    withYear: boolean;
    endMoved: boolean;
  } | null>(null);

  const questionId = useId();
  const confirmingRef = useRef<HTMLSpanElement>(null);
  const setRef = useRef<HTMLButtonElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  // Set when focus should go back to the "Set the event dates" button once it
  // is on the page again.
  const focusSetButton = useRef(false);
  // Set when the saved result is dismissed and focus goes to returnFocusRef.
  const focusReturn = useRef(false);

  useEffect(() => {
    if (step === "confirming") confirmingRef.current?.focus();
    else if (step === "done") resultRef.current?.focus();
    else if (focusSetButton.current) {
      focusSetButton.current = false;
      setRef.current?.focus();
    } else if (focusReturn.current) {
      focusReturn.current = false;
      const target = returnFocusRef?.current;
      if (target?.isConnected && !target.matches(":disabled")) target.focus();
      else fallbackFocusRef?.current?.focus();
    }
  }, [step, returnFocusRef, fallbackFocusRef]);

  // The warning in words, announced when it appears or its days change while
  // the note is on the page. Tracked during render, so the region gets the
  // text in the same commit as the warning. The key leaves the counts out.
  const warningText = info
    ? [
        outsideDatesMessage(info, subject),
        info.offer.kind === "too-long"
          ? tooLongMessage(info.offer, info.withYear)
          : "",
      ]
        .filter(Boolean)
        .join(" ")
    : "";
  const warningKey = info
    ? `${info.offer.kind}|${info.outsideDays.join(",")}`
    : "";
  const [seenWarningKey, setSeenWarningKey] = useState(warningKey);
  const [newWarning, setNewWarning] = useState("");
  if (warningKey !== seenWarningKey) {
    setSeenWarningKey(warningKey);
    setNewWarning(
      warningKey && !busy && step !== "confirming" ? warningText : "",
    );
  }

  async function apply(range: DayRange, withYear: boolean) {
    const ok = await save({ startDate: range.first, endDate: range.last });
    if (!ok) {
      focusSetButton.current = true;
      setStep("idle");
      return;
    }
    setResult({ range, withYear, endMoved: range.last !== event.endDate });
    setStep("done");
    reload();
  }

  const successText = result
    ? `The event ${
        result.range.first === result.range.last ? "date is" : "dates are"
      } now ${rangeLabel(result.range, result.withYear)}.`
    : "";
  const announcement = saving
    ? "Saving the event dates…"
    : step === "done"
      ? successText
      : error
        ? ""
        : newWarning;
  const refusal =
    !saving && step !== "done" && error
      ? `The event dates were not saved. ${error}`
      : "";

  const live = (
    <>
      <p role="status" className="sr-only" data-testid={`${testId}-live`}>
        {announcement}
      </p>
      <p role="alert" className="sr-only" data-testid={`${testId}-alert`}>
        {refusal}
      </p>
    </>
  );

  if (step === "done" && result) {
    return (
      <>
        <div
          ref={resultRef}
          tabIndex={-1}
          className="space-y-2 focus:outline-hidden"
          data-testid={testId}
        >
          <CommandFeedback
            state={{ status: "succeeded" }}
            onReset={() => {
              focusReturn.current = true;
              setStep("idle");
              setResult(null);
            }}
            pendingMessage=""
            successMessage={successText}
            testId={`${testId}-result`}
            announce={false}
          />
          {/* A booked "summary is ready" notification keeps the send time of
              the old end, as after a save on the Settings tab. */}
          {result.endMoved && uid && timeZone && (
            <AfterEventNotificationTimeCheck
              key={`${result.range.last}|${timeZone}`}
              eventId={eventId}
              uid={uid}
              endDate={result.range.last}
              timeZone={timeZone}
            />
          )}
        </div>
        {live}
      </>
    );
  }

  // Each branch returns the same shape, the content and then the region, so
  // React keeps the region's element when the note has nothing to show.
  if (!info)
    return (
      <>
        {null}
        {live}
      </>
    );

  const { offer, withYear } = info;
  const droppedTitles =
    offer.kind === "set" &&
    staleDayTitles(
      eventDays(offer.range.first, offer.range.last).days,
      event.dayTitles,
    ).length > 0;
  const shortens =
    offer.kind === "set" && shortensEvent(info.event, offer.range);

  return (
    <>
      <div className="space-y-2" data-testid={testId}>
        <WarningNote role="none" testId={`${testId}-warning`}>
          <span className="block">{outsideDatesMessage(info, subject)}</span>

          {offer.kind === "too-long" ? (
            <span className="mt-1 block" data-testid={`${testId}-too-long`}>
              {tooLongMessage(offer, withYear)}
            </span>
          ) : (
            <>
              {droppedTitles && (
                <span className="mt-1 block" data-testid={`${testId}-titles`}>
                  Day titles for days that are no longer part of the event are
                  dropped the next time you save the day titles.
                </span>
              )}

              {step === "confirming" ? (
                <span
                  ref={confirmingRef}
                  tabIndex={-1}
                  role="group"
                  aria-labelledby={questionId}
                  className="mt-2 block space-y-2 focus:outline-hidden"
                  data-testid={`${testId}-confirming`}
                >
                  <span
                    id={questionId}
                    className="block font-medium"
                    data-testid={`${testId}-question`}
                  >
                    Replace {rangeLabel(info.event, withYear)} with{" "}
                    {rangeLabel(offer.range, withYear)} as the event{" "}
                    {offer.range.first === offer.range.last ? "date" : "dates"}?
                  </span>
                  {shortens && (
                    <span className="block" data-testid={`${testId}-shortens`}>
                      This shortens the event: reminders and the start
                      notification follow the new start date.
                    </span>
                  )}
                  <span className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      variant="secondary"
                      className={WRAPPING_BUTTON}
                      data-testid={`${testId}-confirm`}
                      disabled={saving}
                      onClick={() => void apply(offer.range, withYear)}
                    >
                      {saving
                        ? "Saving…"
                        : `Yes, use ${dayLabel(offer.range.first, withYear)}${
                            offer.range.first === offer.range.last
                              ? ""
                              : ` to ${dayLabel(offer.range.last, withYear)}`
                          }`}
                    </Button>
                    <Button
                      variant="outline"
                      className={WRAPPING_BUTTON}
                      data-testid={`${testId}-cancel`}
                      disabled={saving}
                      onClick={() => {
                        focusSetButton.current = true;
                        setStep("idle");
                      }}
                    >
                      Cancel
                    </Button>
                  </span>
                </span>
              ) : (
                <span className="mt-2 block">
                  <Button
                    ref={setRef}
                    variant="outline"
                    className={WRAPPING_BUTTON}
                    data-testid={`${testId}-set`}
                    onClick={() => {
                      clearError();
                      setNewWarning("");
                      setStep("confirming");
                    }}
                  >
                    {setDatesLabel(offer.range, withYear)}
                  </Button>
                </span>
              )}
            </>
          )}
        </WarningNote>

        {saving && (
          <CommandFeedback
            state={{ status: "pending" }}
            onReset={() => undefined}
            pendingMessage="Saving the event dates…"
            successMessage=""
            testId={`${testId}-result`}
            announce={false}
          />
        )}
        {error && !saving && (
          <CommandFeedback
            state={{ status: "failed", error }}
            onReset={clearError}
            pendingMessage=""
            successMessage=""
            testId={`${testId}-result`}
            announce={false}
          />
        )}
      </div>
      {live}
    </>
  );
}
